import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import DatePicker from '../../components/DatePicker';
import { apiRequest } from '../../services/api';
import UsageInfo from '../../components/UsageInfo';

// ── Yardımcılar ───────────────────────────────────────────────────────────────
function pad(n) { return String(n).padStart(2, '0'); }
function bugunStr() {
  const d = new Date();
  return `${pad(d.getDate())}.${pad(d.getMonth()+1)}.${d.getFullYear()}`;
}
function yarınStr() {
  const d = new Date(); d.setDate(d.getDate()+1);
  return `${pad(d.getDate())}.${pad(d.getMonth()+1)}.${d.getFullYear()}`;
}
function addDays(str, n) {
  const [g,a,y] = str.split('.').map(Number);
  const d = new Date(y, a-1, g+n);
  return `${pad(d.getDate())}.${pad(d.getMonth()+1)}.${d.getFullYear()}`;
}
function parseLokal(iso) {
  if (!iso) return null;
  if (iso.endsWith('Z') || iso.match(/[+-]\d{2}:\d{2}$/)) return new Date(iso);
  const [dp, tp='00:00'] = iso.split('T');
  const [y,m,g] = dp.split('-').map(Number);
  const [h,mi]  = tp.split(':').map(Number);
  return new Date(y, m-1, g, h, mi);
}
function strToDate(str) {
  if (!str) return null;
  const [g,a,y] = str.split('.').map(Number);
  return new Date(y, a-1, g);
}
function fmtPara(n) {
  return (n||0).toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2});
}
function tarihAyniGunMu(d1, d2) {
  if (!d1 || !d2) return false;
  return d1.getFullYear()===d2.getFullYear() && d1.getMonth()===d2.getMonth() && d1.getDate()===d2.getDate();
}

function ayBasiStr() {
  const d = new Date();
  return `${pad(1)}.${pad(d.getMonth()+1)}.${d.getFullYear()}`;
}

function hesaplaDolulukAralik(rezervasyonlar, aktifOdalar, basStr, bitStr) {
  const basD = strToDate(basStr);
  const bitD = strToDate(bitStr);
  if (!basD || !bitD || bitD < basD || aktifOdalar.length === 0) return 0;
  const gunSayisi = Math.round((bitD - basD) / 86400000) + 1;
  let toplamDoluGun = 0;
  for (let i = 0; i < gunSayisi; i++) {
    const gun = new Date(basD.getFullYear(), basD.getMonth(), basD.getDate() + i);
    const cinOdaIdler = new Set();
    for (const r of rezervasyonlar) {
      if (r.durum === 'İptal') continue;
      const g = r.girisTarihi ? parseLokal(r.girisTarihi) : null;
      if (!g) continue;
      const gG = new Date(g.getFullYear(), g.getMonth(), g.getDate());
      if (gun < gG) continue;
      let cG;
      if (!r.cikisTarihi) {
        cG = r.belirsizCikisKilitGun
          ? new Date(gG.getTime() + r.belirsizCikisKilitGun * 86400000)
          : new Date(9999, 0, 1);
      } else {
        const c = parseLokal(r.cikisTarihi);
        cG = new Date(c.getFullYear(), c.getMonth(), c.getDate());
      }
      if (gun < cG) cinOdaIdler.add(r.odaId);
    }
    toplamDoluGun += aktifOdalar.filter(o => cinOdaIdler.has(o.odaId)).length;
  }
  const maxDoluGun = aktifOdalar.length * gunSayisi;
  return maxDoluGun > 0 ? Math.round((toplamDoluGun / maxDoluGun) * 100) : 0;
}

// ── Avatar ────────────────────────────────────────────────────────────────────
function InitialsAvatar({ name }) {
  const parts = (name || '').split(' ').filter(Boolean);
  const initials = parts.length >= 2 ? parts[0][0] + parts[parts.length-1][0] : (parts[0]?.[0] || '?');
  const colors = ['bg-violet-500','bg-indigo-500','bg-sky-500','bg-emerald-500','bg-amber-500','bg-rose-500'];
  const idx = (name?.charCodeAt(0) || 0) % colors.length;
  return (
    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-2xl text-white text-xs font-black flex-shrink-0 shadow-md ${colors[idx]}`}>
      {initials.toUpperCase()}
    </span>
  );
}

// ── Ana Bileşen ───────────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const [tarih,      setTarih]      = useState(bugunStr());
  const [giris,      setGiris]      = useState(bugunStr());
  const [cikis,      setCikis]      = useState(yarınStr());
  const [dolulukBas, setDolulukBas] = useState(ayBasiStr());
  const [dolulukBit, setDolulukBit] = useState(bugunStr());
  const [yukleniyor, setYukleniyor] = useState(true);
  const [error,      setError]      = useState(null);

  const [odalar,         setOdalar]         = useState([]);
  const [rezervasyonlar, setRezervasyonlar] = useState([]);
  const [bakiyeler,      setBakiyeler]      = useState([]);
  const [odemeler,       setOdemeler]       = useState([]);
  const [kasaGelirleri,  setKasaGelirleri]  = useState([]);
  const [giderler,       setGiderler]       = useState([]);
  const [adisyonlar,     setAdisyonlar]     = useState([]);

  const veriYukle = useCallback(async () => {
    setYukleniyor(true); setError(null);
    try {
      const [odaD, rezD, bakD, odemeD, kasaGelirD, giderD, adisD] = await Promise.all([
        apiRequest('oda').catch(() => []),
        apiRequest('rezervasyonlar').catch(() => []),
        apiRequest('rezervasyonlar/bakiyeler').catch(() => []),
        apiRequest('odemeler').catch(() => []),
        apiRequest('kasa-gelir').catch(() => []),
        apiRequest('kasa-gider').catch(() => []),
        apiRequest('adisyonlar').catch(() => []),
      ]);
      setOdalar((odaD || []).sort((a, b) => String(a.odaNo || '').localeCompare(String(b.odaNo || ''), undefined, { numeric: true, sensitivity: 'base' })));
      setRezervasyonlar(rezD || []);
      setBakiyeler(bakD || []);
      setOdemeler(odemeD || []);
      setKasaGelirleri(kasaGelirD || []);
      setGiderler(giderD || []);
      setAdisyonlar(adisD || []);
    } catch(e) {
      console.error(e);
      setError('Veriler yüklenirken bir hata oluştu.');
    }
    setYukleniyor(false);
  }, []);

  useEffect(() => { veriYukle(); }, [veriYukle]);

  // ── Hesaplamalar ─────────────────────────────────────────────────────────────
  const tarihD = strToDate(tarih);

  const aktifOdalar  = odalar.filter(o => o.grupAktifMi !== false);
  const toplamOda    = aktifOdalar.length;
  const cinOdaIdler  = new Set(rezervasyonlar.filter(r => r.durum === 'C-IN Yapıldı').map(r => r.odaId));
  const doluOda      = aktifOdalar.filter(o => cinOdaIdler.has(o.odaId)).length;
  const kirliOda     = aktifOdalar.filter(o => o.durum === 'Kirli').length;
  const bosOda       = aktifOdalar.filter(o => !cinOdaIdler.has(o.odaId) && o.durum !== 'Kirli' && !o.arizaliMi).length;
  const arizaliOda   = aktifOdalar.filter(o => o.arizaliMi).length;
  const dolulukOrani = toplamOda > 0 ? Math.round((doluOda / toplamOda) * 100) : 0;
  const otomatikUzatma = rezervasyonlar.filter(r =>
    r.otomatikUzatmaAktif && r.durum !== 'İptal' && r.durum !== 'C-OUT Yapıldı'
  ).length;

  const odaDurumData = [
    { name: 'Dolu',    value: doluOda,    color: '#22c55e' },
    { name: 'Temiz',   value: bosOda,     color: '#94a3b8' },
    { name: 'Kirli',   value: kirliOda,   color: '#f97316' },
    { name: 'Arızalı', value: arizaliOda, color: '#ef4444' },
  ].filter(d => d.value > 0);

  const aktifRezler = rezervasyonlar.filter(r => {
    if (r.durum === 'İptal') return false;
    const g = r.girisTarihi ? parseLokal(r.girisTarihi) : null;
    if (!g || !tarihD) return false;
    const tG = new Date(tarihD.getFullYear(), tarihD.getMonth(), tarihD.getDate());
    const gG = new Date(g.getFullYear(), g.getMonth(), g.getDate());
    if (tG < gG) return false;
    // Belirsiz çıkış: kilit günü varsa efektif bitiş hesapla, yoksa süresiz aktif
    if (!r.cikisTarihi) {
      if (r.belirsizCikisKilitGun) {
        const efektifCikis = new Date(gG.getTime() + r.belirsizCikisKilitGun * 86400000);
        return tG < efektifCikis;
      }
      return true; // Süresiz aktif
    }
    const c = parseLokal(r.cikisTarihi);
    const cG = new Date(c.getFullYear(), c.getMonth(), c.getDate());
    return tG < cG;
  });

  const bugunGiris = rezervasyonlar.filter(r => {
    if (r.durum === 'İptal') return false;
    const g = r.girisTarihi ? parseLokal(r.girisTarihi) : null;
    return g && tarihAyniGunMu(g, tarihD);
  });

  const bugunCikis = rezervasyonlar.filter(r => {
    if (r.durum === 'İptal') return false;
    const c = r.cikisTarihi ? parseLokal(r.cikisTarihi) : null;
    return c && tarihAyniGunMu(c, tarihD);
  });

  const gunlukOdemeler      = odemeler.filter(o => { const d = o.odemeTarihi ? parseLokal(o.odemeTarihi) : null; return d && tarihAyniGunMu(d, tarihD); });
  const gunlukKasaGelirleri = kasaGelirleri.filter(g => { const d = g.tarih ? parseLokal(g.tarih) : null; return d && tarihAyniGunMu(d, tarihD); });
  const gunlukAdisyonlar    = adisyonlar.filter(a => { if (!a.odemeyapildiMi) return false; const d = a.olusturmaTarihi ? parseLokal(a.olusturmaTarihi) : null; return d && tarihAyniGunMu(d, tarihD); });
  const gunlukAdisyonToplam = gunlukAdisyonlar.reduce((s,a) => s + (a.toplamTutar||0), 0);
  const gunlukGelirToplam   =
    gunlukOdemeler.reduce((s,o) => s + (o.tutar||0) * (o.kurDegeri||1), 0) +
    gunlukKasaGelirleri.reduce((s,g) => s + (g.tutar||0) * (g.kurDegeri||1), 0) +
    gunlukAdisyonToplam;
  const gunlukGiderler      = giderler.filter(g => { const d = g.tarih ? parseLokal(g.tarih) : null; return d && tarihAyniGunMu(d, tarihD); });
  const gunlukGiderToplam   = gunlukGiderler.reduce((s,g) => s + (g.toplamTutar||0) * (g.kurDegeri||1), 0);
  const netKasa             = gunlukGelirToplam - gunlukGiderToplam;

  const dolulukAralikOrani = useMemo(
    () => hesaplaDolulukAralik(rezervasyonlar, aktifOdalar, dolulukBas, dolulukBit),
    [rezervasyonlar, aktifOdalar, dolulukBas, dolulukBit]
  );

  const gunlukRezOdemeToplam  = gunlukOdemeler.reduce((s,o) => s + (o.tutar||0)*(o.kurDegeri||1), 0);
  const gunlukKasaGelirToplam = gunlukKasaGelirleri.reduce((s,g) => s + (g.tutar||0)*(g.kurDegeri||1), 0);

  const bakiyeBekleyenler = bakiyeler.filter(b => b.kalanBakiye > 0);
  const toplamBakiye      = bakiyeBekleyenler.reduce((s,b) => s + (b.kalanBakiye||0), 0);

  const son7Gun = Array.from({length:7}, (_,i) => { const d = new Date(); d.setDate(d.getDate()-(6-i)); return d; });
  const grafikData = son7Gun.map(gun => {
    const gelir =
      odemeler.filter(o => { const d = o.odemeTarihi ? parseLokal(o.odemeTarihi) : null; return d && tarihAyniGunMu(d, gun); }).reduce((s,o) => s+(o.tutar||0)*(o.kurDegeri||1), 0) +
      kasaGelirleri.filter(g => { const d = g.tarih ? parseLokal(g.tarih) : null; return d && tarihAyniGunMu(d, gun); }).reduce((s,g) => s+(g.tutar||0)*(g.kurDegeri||1), 0) +
      adisyonlar.filter(a => a.odemeyapildiMi && tarihAyniGunMu(parseLokal(a.olusturmaTarihi), gun)).reduce((s,a) => s+(a.toplamTutar||0), 0);
    const gider = giderler.filter(g => { const d = g.tarih ? parseLokal(g.tarih) : null; return d && tarihAyniGunMu(d, gun); }).reduce((s,g) => s+(g.toplamTutar||0)*(g.kurDegeri||1), 0);
    return { gun: `${pad(gun.getDate())}/${pad(gun.getMonth()+1)}`, Gelir: Math.round(gelir), Gider: Math.round(gider) };
  });
  const haftalikGelir = grafikData.reduce((s,d) => s+d.Gelir, 0);
  const haftalikGider = grafikData.reduce((s,d) => s+d.Gider, 0);

  const musaitOdalar = odalar.filter(oda => {
    if (oda.grupAktifMi === false) return false;
    if (oda.arizaliMi || oda.durum === 'Kirli') return false;
    const girisD = strToDate(giris); const cikisD = strToDate(cikis);
    if (!girisD || !cikisD) return true;
    const cakisan = rezervasyonlar.some(r => {
      if (r.odaId !== oda.odaId) return false;
      if (r.durum === 'İptal' || r.durum === 'C-OUT Yapıldı') return false;
      const rG = r.girisTarihi ? parseLokal(r.girisTarihi) : null;
      if (!rG) return false;
      // Belirsiz çıkış: kilit günü varsa efektif bitiş, yoksa süresiz dolu say
      let rC;
      if (!r.cikisTarihi) {
        if (r.belirsizCikisKilitGun) {
          const rGD = new Date(rG.getFullYear(), rG.getMonth(), rG.getDate());
          rC = new Date(rGD.getTime() + r.belirsizCikisKilitGun * 86400000);
        } else {
          return cikisD > rG; // Oda süresiz dolu
        }
      } else {
        rC = parseLokal(r.cikisTarihi);
      }
      return girisD < rC && cikisD > rG;
    });
    return !cakisan;
  }).sort((a, b) => {
    const na = parseInt(a.odaNo, 10), nb = parseInt(b.odaNo, 10);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return String(a.odaNo).localeCompare(String(b.odaNo), 'tr', { numeric: true });
  });

  // ── Ortak label stili ─────────────────────────────────────────────────────
  const LABEL = "text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5";
  const CARD  = "bg-white/95 backdrop-blur-xl rounded-[2.5rem] shadow-2xl border border-white/40 overflow-hidden";

  return (
    <div className="p-4 sm:p-6 space-y-6 animate-fade-in">

      {/* ── Hata ── */}
      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-2xl px-5 py-3.5 text-xs font-black uppercase tracking-widest flex items-center gap-2">
          ⚠ {error}
        </div>
      )}

      {/* ── HEADER ── */}
      <div className="bg-white/95 backdrop-blur-xl rounded-[3rem] p-6 sm:p-8 shadow-2xl border border-white/40 overflow-hidden">

        {/* Başlık + Tarih Nav */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-slate-900 rounded-[1.75rem] flex items-center justify-center text-white text-2xl shadow-2xl flex-shrink-0">📊</div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tighter uppercase leading-none mb-2">Dashboard</h1>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Günlük Özet</p>
            </div>
          </div>

          {/* Tarih Nav */}
          <div className="flex items-center gap-2">
            <button onClick={() => setTarih(t => addDays(t,-1))}
              className="w-11 h-11 rounded-2xl border-2 border-slate-100 bg-white hover:border-indigo-400 text-slate-500 flex items-center justify-center transition-all shadow-sm font-black">
              ‹
            </button>
            <div className="w-40">
              <DatePicker value={tarih} onChange={setTarih}
                className="w-full text-center font-black text-slate-700 bg-white border-2 border-slate-100 focus:border-indigo-400 rounded-2xl px-4 py-2.5 text-xs uppercase tracking-widest outline-none transition-all" />
            </div>
            <button onClick={() => setTarih(t => addDays(t,1))}
              className="w-11 h-11 rounded-2xl border-2 border-slate-100 bg-white hover:border-indigo-400 text-slate-500 flex items-center justify-center transition-all shadow-sm font-black">
              ›
            </button>
            <button onClick={() => setTarih(bugunStr())}
              className="h-11 px-4 rounded-2xl border-2 border-slate-100 bg-white hover:border-indigo-400 text-slate-500 text-[10px] font-black uppercase tracking-widest transition-all hidden sm:flex items-center">
              Bugün
            </button>
            <button onClick={veriYukle}
              className="w-11 h-11 rounded-2xl border-2 border-slate-100 bg-white hover:border-indigo-400 text-slate-500 flex items-center justify-center transition-all shadow-sm text-lg">
              ↺
            </button>
          </div>
        </div>

        {/* Oda Stat Kartları */}
        {yukleniyor ? (
          <div className="flex items-center justify-center py-12 gap-4">
            <div className="w-10 h-10 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Yükleniyor...</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {[
              { label: 'Toplam Oda',  v: toplamOda,           c: 'from-slate-700 to-slate-900',   i: '🏨', onClick: () => navigate('/room-rack') },
              { label: 'Dolu',        v: doluOda,             c: 'from-emerald-400 to-teal-600',  i: '✅', onClick: () => navigate('/room-rack') },
              { label: 'Temiz',       v: bosOda,              c: 'from-sky-400 to-blue-600',      i: '🔓', onClick: () => navigate('/room-rack') },
              { label: 'Kirli',       v: kirliOda,            c: 'from-orange-400 to-amber-600',  i: '🧹', onClick: null },
              { label: 'Arızalı',     v: arizaliOda,          c: 'from-rose-500 to-red-600',      i: '🔧', onClick: null },
              { label: `%${dolulukOrani} Doluluk`, v: aktifRezler.length, c: 'from-violet-500 to-purple-700', i: '📈', sub: 'rezervasyon', onClick: () => navigate('/room-rack') },
            ].map(s => (
              <div key={s.label} onClick={s.onClick || undefined}
                className={`bg-gradient-to-br ${s.c} p-4 rounded-[1.75rem] text-white shadow-xl relative overflow-hidden ${s.onClick ? 'cursor-pointer hover:scale-105 active:scale-95' : 'cursor-default'} transition-all duration-300 hover:shadow-2xl group`}>
                <div className="absolute top-0 right-0 -mr-8 -mt-8 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all duration-500" />
                <span className="absolute top-1.5 right-2.5 opacity-20 text-2xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 relative z-10">{s.i}</span>
                <p className="text-[7px] sm:text-[8px] font-black opacity-60 uppercase tracking-tight mb-0.5 leading-tight relative z-10 break-words">{s.label}</p>
                <h3 className="text-xl sm:text-3xl font-black tracking-tighter relative z-10">{s.v}</h3>
                {s.sub && <p className="text-[8px] opacity-60 font-black uppercase tracking-widest mt-0.5 relative z-10">{s.sub}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {!yukleniyor && (
        <>
          {/* ── ROW 1: Giriş / Çıkış / Bakiye Banner ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Bugün Giriş',          v: bugunGiris.length,           sub: 'rezervasyon',               c: 'from-emerald-500 to-teal-600',   i: '🚪', route: '/rezervasyon' },
              { label: 'Bugün Çıkış',           v: bugunCikis.length,           sub: 'rezervasyon',               c: 'from-fuchsia-500 to-purple-600', i: '🏁', route: '/rezervasyon' },
              { label: 'Bekleyen Bakiye',       v: `${fmtPara(toplamBakiye)} ₺`, sub: `${bakiyeBekleyenler.length} rezervasyon`, c: 'from-rose-500 to-red-600', i: '💳', route: '/kasa/bakiyeler' },
            ].map(s => (
              <div key={s.label} onClick={() => navigate(s.route)}
                className={`bg-gradient-to-br ${s.c} rounded-[2rem] p-5 sm:p-6 flex items-center gap-4 cursor-pointer hover:scale-[1.05] hover:-translate-y-1 hover:shadow-2xl active:scale-[0.95] transition-all duration-300 group shadow-2xl relative overflow-hidden`}>
                <div className="absolute top-0 right-0 -mr-12 -mt-12 w-32 h-32 bg-white/10 rounded-full blur-3xl group-hover:bg-white/20 transition-all duration-500 text-white" />
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 shadow-inner border border-white/30 relative z-10 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">{s.i}</div>
                <div className="min-w-0 relative z-10">
                  <p className="text-2xl sm:text-3xl font-black text-white tracking-tighter truncate">{s.v}</p>
                  <p className="text-[9px] font-black text-white/70 uppercase tracking-widest mt-0.5">{s.label}</p>
                  <p className="text-[8px] font-black text-white/50 uppercase tracking-widest">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ── Doluluk Analizi ── */}
          <div className={CARD}>
            <div className="flex items-center justify-between px-7 py-5 bg-gradient-to-r from-violet-600 to-purple-700">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg border border-white/30">📊</div>
                <div>
                  <h2 className="font-black text-white text-sm uppercase tracking-widest">Doluluk Analizi</h2>
                  <p className="text-violet-200 text-[9px] font-black uppercase tracking-widest">{dolulukBas} — {dolulukBit}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-violet-300 text-[9px] font-black uppercase tracking-widest mb-0.5">Aralık Ortalaması</p>
                <p className="text-white text-3xl font-black tracking-tighter">%{dolulukAralikOrani}</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Başlangıç</label>
                  <DatePicker value={dolulukBas} onChange={setDolulukBas}
                    className="w-full bg-slate-50 border-2 border-transparent focus:border-violet-400 focus:bg-white rounded-2xl px-4 py-2.5 text-xs font-black text-slate-700 outline-none transition-all" />
                </div>
                <div>
                  <label className={LABEL}>Bitiş</label>
                  <DatePicker value={dolulukBit} onChange={setDolulukBit}
                    className="w-full bg-slate-50 border-2 border-transparent focus:border-violet-400 focus:bg-white rounded-2xl px-4 py-2.5 text-xs font-black text-slate-700 outline-none transition-all" />
                </div>
              </div>
              <div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                  <div className="h-3 bg-gradient-to-r from-violet-500 to-purple-600 rounded-full transition-all duration-700"
                    style={{ width: `${dolulukAralikOrani}%` }} />
                </div>
                <div className="flex justify-between mt-1.5">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">0%</span>
                  <span className="text-[9px] font-black text-violet-600 uppercase tracking-widest">%{dolulukAralikOrani} Ortalama</span>
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">100%</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-slate-50 rounded-2xl p-3">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Toplam Oda</p>
                  <p className="text-lg font-black text-slate-700">{toplamOda}</p>
                </div>
                <div className="bg-emerald-50 rounded-2xl p-3">
                  <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest mb-0.5">Anlık Doluluk</p>
                  <p className="text-lg font-black text-emerald-700">%{dolulukOrani}</p>
                </div>
                <div className="bg-violet-50 rounded-2xl p-3">
                  <p className="text-[8px] font-black text-violet-400 uppercase tracking-widest mb-0.5">Aralık Doluluk</p>
                  <p className="text-lg font-black text-violet-700">%{dolulukAralikOrani}</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── ROW 2: Günlük Kasa  +  Oda Durumu & Müsaitlik ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Günlük Kasa */}
            <div className={CARD}>
              <div className="flex items-center justify-between px-7 py-5 bg-gradient-to-r from-indigo-600 to-violet-600">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg border border-white/30">💰</div>
                  <div>
                    <h2 className="font-black text-white text-sm uppercase tracking-widest">Günlük Kasa</h2>
                    <p className="text-indigo-200 text-[9px] font-black uppercase tracking-widest">{tarih}</p>
                  </div>
                </div>
                <button onClick={() => navigate('/kasa/gunluk')}
                  className="text-[9px] font-black text-indigo-200 hover:text-white uppercase tracking-widest transition-colors">
                  Detay →
                </button>
              </div>

              <div className="p-6 space-y-5">
                {/* Gelir / Gider / Net */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 text-center">
                    <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest mb-1">Gelir</p>
                    <p className="text-base font-black text-emerald-700 leading-tight">{fmtPara(gunlukGelirToplam)}</p>
                    <p className="text-[8px] font-black text-emerald-400 mt-0.5 uppercase">₺</p>
                  </div>
                  <div className="bg-rose-50 border border-rose-100 rounded-2xl p-4 text-center">
                    <p className="text-[8px] font-black text-rose-500 uppercase tracking-widest mb-1">Gider</p>
                    <p className="text-base font-black text-rose-700 leading-tight">{fmtPara(gunlukGiderToplam)}</p>
                    <p className="text-[8px] font-black text-rose-400 mt-0.5 uppercase">₺</p>
                  </div>
                  <div className={`${netKasa >= 0 ? 'bg-indigo-50 border-indigo-100' : 'bg-rose-50 border-rose-100'} border rounded-2xl p-4 text-center`}>
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Net</p>
                    <p className={`text-base font-black leading-tight ${netKasa >= 0 ? 'text-indigo-700' : 'text-rose-700'}`}>{fmtPara(netKasa)}</p>
                    <p className="text-[8px] font-black text-slate-400 mt-0.5 uppercase">₺</p>
                  </div>
                </div>

                {/* Gelir Kaynak Dağılımı */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Rezervasyon', val: gunlukRezOdemeToplam,  count: gunlukOdemeler.length,       color: 'bg-indigo-50 border-indigo-100 text-indigo-600' },
                    { label: 'Kasa',        val: gunlukKasaGelirToplam, count: gunlukKasaGelirleri.length,  color: 'bg-emerald-50 border-emerald-100 text-emerald-600' },
                    { label: 'Adisyon',     val: gunlukAdisyonToplam,   count: gunlukAdisyonlar.length,     color: 'bg-amber-50 border-amber-100 text-amber-600' },
                  ].map(s => (
                    <div key={s.label} className={`border rounded-2xl p-3 ${s.color}`}>
                      <p className="text-[8px] font-black uppercase tracking-widest opacity-60 mb-0.5">{s.label}</p>
                      <p className="text-[10px] font-black leading-tight">{fmtPara(s.val)} ₺</p>
                      <p className="text-[8px] font-black opacity-50">{s.count} işlem</p>
                    </div>
                  ))}
                </div>

                {/* Günlük Gelirler mini liste */}
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2.5">Son Gelirler</p>
                  <div className="space-y-1.5">
                    {[...gunlukOdemeler, ...gunlukKasaGelirleri, ...gunlukAdisyonlar].slice(0,4).length === 0 ? (
                      <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest py-3 text-center">Bu tarihte gelir yok</p>
                    ) : [...gunlukOdemeler.map(o => ({ ac: o.aciklama || '—', tutar: (o.tutar||0)*(o.kurDegeri||1), tip: o.odemeTipi || '—' })),
                          ...gunlukKasaGelirleri.map(g => ({ ac: g.aciklama || '—', tutar: (g.tutar||0)*(g.kurDegeri||1), tip: g.odemeTipi || '—' })),
                          ...gunlukAdisyonlar.map(a => ({ ac: a.stokAdi || 'Adisyon', tutar: a.toplamTutar||0, tip: 'Adisyon' })),
                        ].slice(0,4).map((item, i) => (
                      <div key={i} className="flex items-start justify-between bg-slate-50 rounded-2xl px-4 py-2.5 gap-2">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0 mt-1" />
                          <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest break-words">{item.ac}</span>
                        </div>
                        <div className="flex flex-col items-end shrink-0">
                          <span className="text-[9px] font-black text-slate-400 uppercase whitespace-nowrap">{item.tip}</span>
                          <span className="text-[10px] font-black text-emerald-600 whitespace-nowrap">{fmtPara(item.tutar)} ₺</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Günlük Giderler mini liste */}
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2.5">Son Giderler</p>
                  <div className="space-y-1.5">
                    {gunlukGiderler.slice(0,3).length === 0 ? (
                      <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest py-3 text-center">Bu tarihte gider yok</p>
                    ) : gunlukGiderler.slice(0,3).map((g, i) => (
                      <div key={i} className="flex items-start justify-between bg-slate-50 rounded-2xl px-4 py-2.5 gap-2">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0 mt-1" />
                          <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest break-words">{g.aciklama || '—'}</span>
                        </div>
                        <span className="text-[10px] font-black text-rose-600 shrink-0 whitespace-nowrap">{fmtPara((g.toplamTutar||0)*(g.kurDegeri||1))} ₺</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Oda Durumu + Müsaitlik */}
            <div className="space-y-4">

              {/* Oda Durumu */}
              <div className={CARD}>
                <div className="flex items-center justify-between px-7 py-5 bg-gradient-to-r from-indigo-600 to-violet-600">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg border border-white/30">🏨</div>
                    <h2 className="font-black text-white text-sm uppercase tracking-widest">Oda Durumu</h2>
                  </div>
                  <button onClick={() => navigate('/room-rack')}
                    className="text-[9px] font-black text-indigo-200 hover:text-white uppercase tracking-widest transition-colors">
                    Oda Planı →
                  </button>
                </div>
                <div className="p-4 sm:p-6 flex items-center gap-3 sm:gap-6">
                  <div className="w-24 sm:w-32 flex-shrink-0">
                    <ResponsiveContainer width="100%" height={110}>
                      <PieChart>
                        <Pie data={odaDurumData.length > 0 ? odaDurumData : [{name:'Oda yok',value:1}]}
                          cx="50%" cy="50%" innerRadius={30} outerRadius={50} dataKey="value">
                          {odaDurumData.map((e,i) => <Cell key={i} fill={e.color}/>)}
                        </Pie>
                        <Tooltip formatter={v => `${v} oda`}/>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 min-w-0 space-y-2.5">
                    {[
                      { label:'Dolu',    val:doluOda,    color:'bg-emerald-500' },
                      { label:'Temiz',   val:bosOda,     color:'bg-slate-400' },
                      { label:'Kirli',   val:kirliOda,   color:'bg-orange-400' },
                      { label:'Arızalı', val:arizaliOda, color:'bg-rose-400' },
                    ].map(d => (
                      <div key={d.label} className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${d.color}`}/>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-tight flex-shrink-0">{d.label}</span>
                        <span className="text-[10px] font-black text-slate-700 w-5 text-right flex-shrink-0">{d.val}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-1.5 min-w-0">
                          <div className={`h-1.5 rounded-full ${d.color}`}
                            style={{width: toplamOda > 0 ? `${(d.val/toplamOda)*100}%` : '0%'}}/>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {otomatikUzatma > 0 && (
                  <div className="mx-6 mb-5 flex items-center gap-2 bg-teal-50 border border-teal-100 rounded-2xl px-4 py-2.5">
                    <span className="text-teal-500">🔄</span>
                    <span className="text-[10px] font-black text-teal-700 uppercase tracking-widest">{otomatikUzatma} rezervasyon otomatik uzatma aktif</span>
                  </div>
                )}
              </div>

              {/* Hızlı Müsaitlik */}
              <div className={CARD}>
                <div className="flex items-center justify-between px-7 py-5 bg-gradient-to-r from-emerald-500 to-teal-600">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg border border-white/30">🔍</div>
                    <h2 className="font-black text-white text-sm uppercase tracking-widest">Hızlı Müsaitlik</h2>
                  </div>
                  <span className="bg-white/20 text-white text-[10px] font-black px-3 py-1.5 rounded-xl uppercase tracking-widest border border-white/30">
                    {musaitOdalar.length} oda
                  </span>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className={LABEL}>Giriş</label>
                      <DatePicker value={giris} onChange={setGiris}
                        className="w-full bg-slate-50 border-2 border-transparent focus:border-emerald-400 focus:bg-white rounded-2xl px-4 py-2.5 text-xs font-black text-slate-700 outline-none transition-all" />
                    </div>
                    <div>
                      <label className={LABEL}>Çıkış</label>
                      <DatePicker value={cikis} onChange={setCikis}
                        className="w-full bg-slate-50 border-2 border-transparent focus:border-emerald-400 focus:bg-white rounded-2xl px-4 py-2.5 text-xs font-black text-slate-700 outline-none transition-all" />
                    </div>
                  </div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">
                    Müsait Odalar — {musaitOdalar.length} / {aktifOdalar.length} oda
                  </p>
                  <div className="max-h-44 overflow-y-auto custom-scrollbar pr-1">
                    {musaitOdalar.length === 0
                      ? <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest py-4 text-center">Bu tarihte müsait oda yok</p>
                      : <div className="flex flex-wrap gap-2">
                          {musaitOdalar.map(o => (
                            <button key={o.odaId} onClick={() => navigate('/room-rack')}
                              className="bg-gradient-to-br from-indigo-600 to-violet-600 hover:from-emerald-500 hover:to-teal-500 text-white text-[10px] font-black min-w-[2.75rem] h-11 px-2 rounded-2xl transition-all shadow-lg shadow-indigo-100 hover:scale-110 active:scale-95">
                              {o.odaNo}
                            </button>
                          ))}
                        </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── ROW 3: Grafik + Bakiye Bekleyenler ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Son 7 Gün Grafik */}
            <div className={CARD}>
              <div className="flex items-center justify-between px-7 py-5 bg-gradient-to-r from-indigo-600 to-violet-600">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg border border-white/30">📈</div>
                  <h2 className="font-black text-white text-sm uppercase tracking-widest">Son 7 Gün</h2>
                </div>
                <button onClick={() => navigate('/kasa/aylik')}
                  className="text-[9px] font-black text-indigo-200 hover:text-white uppercase tracking-widest transition-colors">
                  Aylık →
                </button>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={grafikData} margin={{top:5, right:10, bottom:5, left:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                    <XAxis dataKey="gun" tick={{fontSize:9, fontWeight:900, fill:'#94a3b8'}} tickLine={false}/>
                    <YAxis tick={{fontSize:9, fontWeight:900, fill:'#94a3b8'}} tickLine={false} axisLine={false}
                      tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}/>
                    <Tooltip formatter={v => `${v.toLocaleString('tr-TR')} ₺`} contentStyle={{borderRadius:'1rem', border:'none', boxShadow:'0 20px 60px -10px rgba(0,0,0,0.15)', fontSize:11, fontWeight:900}}/>
                    <Bar dataKey="Gelir" fill="#6366f1" radius={[6,6,0,0]}/>
                    <Bar dataKey="Gider" fill="#f43f5e" radius={[6,6,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-6 mt-3">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-md bg-indigo-500 block"/>
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Gelir: {fmtPara(haftalikGelir)} ₺</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-md bg-rose-400 block"/>
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Gider: {fmtPara(haftalikGider)} ₺</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bakiye Bekleyenler */}
            <div className={CARD}>
              <div className="flex items-center justify-between px-7 py-5 bg-gradient-to-r from-rose-600 to-red-600">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg border border-white/30">⚠</div>
                  <div>
                    <h2 className="font-black text-white text-sm uppercase tracking-widest">Bakiye Bekleyenler</h2>
                    <p className="text-rose-200 text-[9px] font-black uppercase tracking-widest mt-0.5">{bakiyeBekleyenler.length} rezervasyon · {fmtPara(toplamBakiye)} ₺</p>
                  </div>
                </div>
                <button onClick={() => navigate('/kasa/bakiyeler')}
                  className="text-[9px] font-black text-rose-200 hover:text-white uppercase tracking-widest transition-colors">
                  Tümü →
                </button>
              </div>
              <div className="p-6">
                {bakiyeBekleyenler.length === 0 ? (
                  <div className="text-center py-8 flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center text-2xl">🎉</div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Bakiye bekleyen rezervasyon yok</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {bakiyeBekleyenler.slice(0,6).map((b, i) => (
                      <div key={i} className="flex items-center gap-3 bg-rose-50/50 hover:bg-rose-50 rounded-2xl px-4 py-3 transition-colors">
                        <InitialsAvatar name={b.adSoyad} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-black text-slate-800 uppercase tracking-tight truncate">{b.adSoyad || '—'}</p>
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Oda {b.odaNo || '—'}</p>
                        </div>
                        <span className="text-[10px] font-black text-rose-600 shrink-0">{fmtPara(b.kalanBakiye)} ₺</span>
                      </div>
                    ))}
                    {bakiyeBekleyenler.length > 6 && (
                      <p className="text-[9px] font-black text-slate-400 text-center uppercase tracking-widest pt-1">+{bakiyeBekleyenler.length - 6} daha var</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── ROW 4: Bugün Giriş / Bugün Çıkış ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Bugün Giriş */}
            <div className={CARD}>
              <div className="flex items-center justify-between px-7 py-5 bg-gradient-to-r from-emerald-500 to-teal-600">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg border border-white/30">🚪</div>
                  <div>
                    <h2 className="font-black text-white text-sm uppercase tracking-widest">Bugün Giriş</h2>
                    <p className="text-emerald-100 text-[9px] font-black uppercase tracking-widest mt-0.5">{bugunGiris.length} rezervasyon</p>
                  </div>
                </div>
                <button onClick={() => navigate('/rezervasyon')}
                  className="text-[9px] font-black text-emerald-100 hover:text-white uppercase tracking-widest transition-colors">
                  Tümü →
                </button>
              </div>
              <div className="p-6">
                {bugunGiris.length === 0 ? (
                  <div className="text-center py-8 flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-2xl">🚪</div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Bugün giriş yok</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {bugunGiris.slice(0,5).map((r, i) => (
                      <div key={i} className="flex items-center gap-3 bg-slate-50 hover:bg-emerald-50/50 rounded-2xl px-4 py-3 transition-colors">
                        <InitialsAvatar name={r.rezervasyonMisafirler?.[0]?.misafir?.adSoyad} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-black text-slate-800 uppercase tracking-tight truncate">
                            {r.rezervasyonMisafirler?.[0]?.misafir?.adSoyad || '—'}
                          </p>
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Oda {r.odaNo || '—'}</p>
                        </div>
                        <span className={`text-[9px] font-black px-2.5 py-1 rounded-xl uppercase tracking-widest ${r.durum?.includes('C-IN') ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'}`}>
                          {r.durum}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Bugün Çıkış */}
            <div className={CARD}>
              <div className="flex items-center justify-between px-7 py-5 bg-gradient-to-r from-fuchsia-600 to-purple-600">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg border border-white/30">🏁</div>
                  <div>
                    <h2 className="font-black text-white text-sm uppercase tracking-widest">Bugün Çıkış</h2>
                    <p className="text-fuchsia-100 text-[9px] font-black uppercase tracking-widest mt-0.5">{bugunCikis.length} rezervasyon</p>
                  </div>
                </div>
                <button onClick={() => navigate('/rezervasyon')}
                  className="text-[9px] font-black text-fuchsia-100 hover:text-white uppercase tracking-widest transition-colors">
                  Tümü →
                </button>
              </div>
              <div className="p-6">
                {bugunCikis.length === 0 ? (
                  <div className="text-center py-8 flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-2xl">🏁</div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Bugün çıkış yok</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {bugunCikis.slice(0,5).map((r, i) => (
                      <div key={i} className="flex items-center gap-3 bg-slate-50 hover:bg-fuchsia-50/50 rounded-2xl px-4 py-3 transition-colors">
                        <InitialsAvatar name={r.rezervasyonMisafirler?.[0]?.misafir?.adSoyad} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-black text-slate-800 uppercase tracking-tight truncate">
                            {r.rezervasyonMisafirler?.[0]?.misafir?.adSoyad || '—'}
                          </p>
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Oda {r.odaNo || '—'}</p>
                        </div>
                        <span className={`text-[9px] font-black px-2.5 py-1 rounded-xl uppercase tracking-widest ${r.durum?.includes('C-OUT') ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-amber-100 text-amber-700'}`}>
                          {r.durum}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <UsageInfo
        id="dashboard-yardim"
        color="indigo"
        title="Nasıl Kullanılır?"
        description="Dashboard sayfası, otelinizin günlük operasyonel ve finansal durumunu tek bir ekrandan takip etmenizi sağlar. Veriler seçilen güne göre anlık güncellenir."
        scenarios={[
          { title: "Giriş & Çıkış Takibi", content: "Otele bugün giriş yapacak veya ayrılacak misafir sayılarını üstteki renkli banner kartlar üzerinden anlık görebilirsiniz." },
          { title: "Kasa ve Finansal Durum", content: "Günlük gelir, gider ve net kasa durumunu grafikler ve listeler üzerinden takip edebilir, toplam bekleyen bakiye miktarını izleyebilirsiniz." },
          { title: "Oda ve Doluluk Analizi", content: "Odaların doluluk oranlarını, temizlik/arıza durumlarını ve hızlı müsaitlik sorgulama aracı ile boş odaları anında sorgulayabilirsiniz." }
        ]}
      />

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        .animate-fade-in  { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </div>
  );
}
